import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isValidHotel } from '../utils/hotelValidation';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const HotelValidationWrapper = ({ children }) => {
    const { t } = useTranslation();
    const { hotelId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [isValidating, setIsValidating] = useState(true);
    const [isValid, setIsValid] = useState(true);
    const [hasPermission, setHasPermission] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const validateHotelAndPermissions = async () => {
            if (!hotelId) {
                setIsValidating(false);
                return;
            }

            setIsValidating(true);
            setError('');

            try {
                // First check if the hotel exists
                const valid = await isValidHotel(hotelId);
                setIsValid(valid);

                if (!valid) {
                    setError(t('permissions.validation.hotelNotFoundMessage', { hotelId }));
                    setIsValidating(false);
                    return;
                }

                // If user is logged in, check permissions
                if (currentUser) {
                    try {
                        const idTokenResult = await currentUser.getIdTokenResult(true);
                        const claims = idTokenResult.claims;
                        const accessibleHotels = claims.accessibleHotels || [];

                        // Check if user has access to this hotel
                        const userHasAccess = Array.isArray(accessibleHotels) && accessibleHotels.includes(hotelId);
                        setHasPermission(userHasAccess);

                        if (!userHasAccess) {
                            setError(t('permissions.validation.noPermissionMessage', { hotelId }));
                        }
                    } catch (claimsError) {
                        console.error('Error fetching user claims:', claimsError);
                        setError(t('permissions.validation.permissionError'));
                        setHasPermission(false);
                    }
                } else {
                    // For non-logged-in users, allow access (they can still report lost items)
                    setHasPermission(true);
                }
            } catch (err) {
                console.error('Error validating hotel:', err);
                setError('Unable to validate hotel. Please try again later.');
                setIsValid(false);
            } finally {
                setIsValidating(false);
            }
        };

        validateHotelAndPermissions();
    }, [hotelId, currentUser, t]);

    if (isValidating) {
        return (
            <div className="hotel-validation-container">
                <div className="loading-container">
                    <h3>{t('permissions.validation.loading')}</h3>
                    <p>{t('permissions.validation.loadingDescription')}</p>
                </div>
            </div>
        );
    }

    if (!isValid) {
        return (
            <div className="hotel-validation-container">
                <div className="error-container">
                    <h2>{t('permissions.validation.hotelNotFound')}</h2>
                    <div className="error-message">
                        <p>{error}</p>
                        <div className="error-actions">
                            <button
                                onClick={() => navigate('/')}
                                className="btn-primary"
                            >
                                {t('permissions.actions.returnToSelection')}
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                className="btn-secondary"
                            >
                                {t('permissions.actions.tryAgain')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!hasPermission) {
        return (
            <div className="hotel-validation-container">
                <div className="error-container">
                    <h2>{t('permissions.validation.accessDenied')}</h2>
                    <div className="error-message">
                        <p>{error}</p>
                        <p>
                            {currentUser ? (
                                t('permissions.validation.loggedInAs', { email: currentUser.email })
                            ) : (
                                t('permissions.validation.needLogin')
                            )}
                        </p>
                        <div className="error-actions">
                            <button
                                onClick={() => navigate('/')}
                                className="btn-primary"
                            >
                                {t('permissions.actions.returnToSelection')}
                            </button>
                            {currentUser ? (
                                <button
                                    onClick={() => navigate('/login')}
                                    className="btn-secondary"
                                >
                                    {t('permissions.actions.switchAccount')}
                                </button>
                            ) : (
                                <button
                                    onClick={() => navigate('/login')}
                                    className="btn-secondary"
                                >
                                    {t('permissions.actions.login')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return children;
};

export default HotelValidationWrapper; 